import { messageKey, useI18n } from "@baton/i18n";
import { House, Inbox, LayoutDashboard, ListTodo, MessageSquare, Search } from "@baton/icons";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useLocation } from "react-router";
import { meQuery } from "../../app/queries.ts";
import { useRuntime } from "../../app/RuntimeProvider.tsx";
import type { ModeDestination, ModeSidebarProps } from "../modes/modes.tsx";
import { GlideList } from "./GlideList.tsx";
import { isWorkspaceMember } from "../state/permissions.ts";
import { SIDEBAR_PILL, SidebarItem, SidebarSection } from "./Sidebar.tsx";

/**
 * FR-015-002, `contracts/screens.md` § Sidebar § Home: the Home mode's tree, two sections
 * (2026-09-17, by the owner's decision): what is yours, Home, the mode's front page (named the
 * Dashboard and in the Team's section from 2026-09-28 until 2026-10-21, when the owner's redesign made
 * it the reader's day), Inbox with its unread count, My work and Search (2026-10-03); then the Team's,
 * Overview, how the Team is doing (2026-10-21, Home's Team tab until the owner's decision later that day
 * that Home is the reader's own), and Meetings, which covers the client meetings and the internal ones alike. The Team's tasks, sprints
 * and projects left this tree for the Work mode on 2026-10-19 (`WorkSidebar.tsx`), by the owner's
 * decision after feedback that the product read as a software-development tool with its sprints in
 * Home; the Clients row had left for the Clients mode on 2026-09-19. The Inbox row carries the
 * unread total the shell reads (SC-015-008).
 */
interface HomeRow extends ModeDestination {
  readonly icon: ReactNode;
  /** Names the row current, for a row whose destination shares a prefix with another; the router decides when absent. */
  readonly current?: (pathname: string, ws: string) => boolean;
}

const PERSONAL_ROWS: readonly HomeRow[] = [
  // Home's front page (2026-10-21, FR-015-162 as amended): the section's first row and the route a Home tile and `/{ws}` land on.
  { id: "dashboard", label: messageKey("nav.home"), path: "dashboard", icon: <House /> },
  { id: "inbox", label: messageKey("nav.inbox"), path: "inbox", icon: <Inbox /> },
  { id: "my-work", label: messageKey("nav.myWork"), path: "my-work", icon: <ListTodo /> },
  // The Team's search, reached from the sidebar as from the palette's See all results and `g s` (2026-10-03, by the owner's approval of the product review: "Search can't be reached").
  { id: "search", label: messageKey("nav.search"), path: "search", icon: <Search /> },
];

const TEAM_ROWS: readonly HomeRow[] = [
  // Overview (2026-10-21, FR-015-166 as amended): the section's first row, for every reader, a guest included.
  { id: "overview", label: messageKey("nav.overview"), path: "overview", icon: <LayoutDashboard /> },
  // 018's meeting review (2026-09-27): the meetings list, current there and on every meeting page, absent for a guest, who reads no meetings.
  { id: "meetings", label: messageKey("nav.meetings"), path: "meetings", icon: <MessageSquare />, memberOnly: true, current: (pathname, ws) => pathname === `/${ws}/meetings` || pathname.startsWith(`/${ws}/meetings/`) },
];

/** Every destination the Home sidebar lists, named as its screen is. */
export const HOME_DESTINATIONS: readonly ModeDestination[] = [...PERSONAL_ROWS, ...TEAM_ROWS];

export function HomeSidebar({ ws, unread, onNavigate }: ModeSidebarProps) {
  const { t } = useI18n();
  const runtime = useRuntime();
  const { pathname } = useLocation();
  const me = useQuery(meQuery(runtime));
  const member = isWorkspaceMember(me.data, ws);
  const navigateProps = onNavigate === undefined ? {} : { onNavigate };
  const row = (entry: HomeRow) => (
    <SidebarItem
      key={entry.id}
      to={`/${ws}/${entry.path}`}
      label={t(entry.label)}
      icon={entry.icon}
      {...(entry.current === undefined ? {} : { current: entry.current(pathname, ws) })}
      {...(entry.id === "inbox" ? { count: unread, countLabel: t("nav.unread", { count: unread }) } : {})}
      {...navigateProps}
    />
  );
  return (
    <>
      <SidebarSection id="personal" title={t("nav.section.personal")}>
        <GlideList className="b-sidebar__list" pillClassName={SIDEBAR_PILL} data-sidebar-destinations>
          {PERSONAL_ROWS.map(row)}
        </GlideList>
      </SidebarSection>
      <SidebarSection id="workspace" title={t("nav.section.workspace")}>
        <GlideList className="b-sidebar__list" pillClassName={SIDEBAR_PILL} data-sidebar-destinations>
          {TEAM_ROWS.filter((entry) => entry.memberOnly !== true || member).map(row)}
        </GlideList>
      </SidebarSection>
    </>
  );
}
